import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Stack,
  Typography,
} from "@mui/joy";
import { useProject } from "../../redux/project/projectHooks";
import {
  formOpenSelector,
  selectedProjectID,
  setFormOpen,
} from "../../redux/filmsReducer";
import { Project_Entity } from "../../entities/project";
import {
  useCreateProjectMutation,
  useDeleteProjectMutation,
  useUpdateProjectMutation,
} from "../../redux/project/projectEndpoints";
import { useAskQuestion } from "../askQuestion/askQuestion";
import { useProjectTypes } from "../../redux/projectType/projectTypeHooks";
import { useNavigate } from "react-router-dom";

/**
 *
 * @returns {ReactElement} FormProject page
 */
export function FormProject() {
  const projectID = useSelector(selectedProjectID);
  const { project } = useProject(projectID);
  const open = useSelector(formOpenSelector("project"));
  const [_project, setProject] = useState<Partial<Project_Entity>>({});

  const [createProject] = useCreateProjectMutation();
  const [updateProject] = useUpdateProjectMutation();
  const [deleteProject] = useDeleteProjectMutation();
  const { projectTypes } = useProjectTypes();
  const sortedProjectTypes = [...(projectTypes ?? [])].sort(
    (a, b) => b.id - a.id
  );
  // Resolve the current value against every project type, but only offer the
  // ones still in use: a project already booked against an archived type keeps
  // showing it, while nobody can newly pick a rate that has been retired.
  const selectedProjectType =
    sortedProjectTypes.find((type) => type.id === _project?.projectTypeID) ??
    null;
  const projectTypeOptions = sortedProjectTypes.filter(
    (type) => !type.archived || type.id === selectedProjectType?.id
  );

  const askQuestion = useAskQuestion();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    setProject(project ?? {});
  }, [project, open]);

  const onClose = () => {
    dispatch(setFormOpen({ isOpen: false, formID: "project" }));
  };

  const save = async () => {
    if (!_project) return;

    if (projectID) {
      await updateProject({ id: projectID, body: _project });
    } else {
      const r: any = await createProject(_project).unwrap();
      navigate(`/projects/${r.id}`);
    }

    onClose();
  };

  const askDelete = () => {
    askQuestion("Are you sure?", ["Cancel", "Yes"], {
      subtitle: (
        <Typography>
          You are about to delete a Project. Please confirm this action.
        </Typography>
      ),
    }).then((i) => {
      if (i == 1) confirmDelete();
    });
  };

  const confirmDelete = async () => {
    if (projectID) await deleteProject(projectID);
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog layout="center" sx={{ minWidth: 420 }}>
        <ModalClose onClick={onClose} />
        <Stack spacing={2}>
          <Box>
            <Typography
              startDecorator={<i className="fa-solid fa-folder-open" />}
              level="h6"
              sx={{ fontWeight: "bold" }}
            >
              {projectID ? "Edit Project" : "Create Project"}
            </Typography>
          </Box>

          <Stack spacing={1}>
            <Stack spacing={1.5}>
              <Input
                startDecorator="Project Name:"
                autoFocus
                value={_project?.name ?? ""}
                onChange={(e) =>
                  setProject((v) => ({ ...v, name: e.target.value }))
                }
                required
              />

              <Input
                value={_project?.referenceNumber ?? ""}
                onChange={(e) =>
                  setProject((v) => ({
                    ...v,
                    referenceNumber: e.target.value,
                  }))
                }
                startDecorator="Reference Number:"
              />

              <Autocomplete
                key={selectedProjectType?.id}
                startDecorator="Project Type:"
                options={projectTypeOptions}
                value={selectedProjectType}
                getOptionLabel={(option) => option?.name ?? ""}
                isOptionEqualToValue={(option, value) =>
                  option.id === value?.id
                }
                slotProps={{
                  listbox: { sx: { zIndex: 9999 } },
                }}
                onChange={(_, value) =>
                  setProject((v) => ({
                    ...v,
                    projectTypeID: value?.id ?? undefined,
                  }))
                }
              />

              <Checkbox
                label="Union Project"
                checked={Boolean(_project?.union)}
                onChange={(e) =>
                  setProject((v) => ({ ...v, union: e.target.checked }))
                }
              />
            </Stack>
          </Stack>

          <Stack direction="row" spacing={1} justifyContent="flex-end">
            {projectID ? (
              <Button variant="soft" onClick={askDelete} color="danger">
                Delete
              </Button>
            ) : null}
            <Button onClick={onClose} color="neutral" variant="soft">
              Cancel
            </Button>
            <Button onClick={save}>{projectID ? "Save" : "Create"}</Button>
          </Stack>
        </Stack>
      </ModalDialog>
    </Modal>
  );
}
